import { CommonModule, formatDate } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TooltipModule } from 'primeng/tooltip';
import { OperatorIssueReportsService } from '../../../api/api-services/operator-issue-reports.service';
import { OperatorAppRatingsService } from '../../../api/api-services/operator-app-ratings.service';
import { OperatorSupportReplyService } from '../../../api/api-services/operator-support-reply.service';
import { OperatorSupportHandledService } from '../../../api/api-services/operator-support-handled.service';
import { IOperatorIssueReportVm } from '../../../api/operator-issue-report-vm.model';
import { IOperatorAppRatingVm } from '../../../api/operator-app-rating-vm.model';
import { ISupportReplyVm } from '../../../api/support-reply-vm.model';
import { API_URL } from '../../../api/models/api-url.token';
import { ClientDeviceComponent } from '../../shared/components/client-device/client-device.component';
import {
  buildReplyBody,
  IReplyThreadItem,
  ReplyDialogComponent,
} from '../../shared/components/reply-dialog/reply-dialog.component';
import { UserAccountLinkComponent } from '../../shared/components/user-account-link/user-account-link.component';

type SupportItem = IOperatorIssueReportVm | IOperatorAppRatingVm;
type SupportKind = 'issue' | 'rating';

interface IReplyTarget {
  issueReportId?: number;
  appRatingId?: number;
  email: string;
  userGuid?: string;
  thread: IReplyThreadItem[];
}

/**
 * Problem reports and app ratings sent from the app, each with the device it was sent from
 * (model, system, app version, screen) and a link to the reporting user's account, and e-mail
 * answers to them. Replaces the fas-admin library's "ratings" page, which shows the e-mail only.
 */
@Component({
  selector: 'app-support-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, ProgressSpinnerModule, SelectButtonModule, TableModule, TabViewModule,
    TooltipModule,
    ClientDeviceComponent, UserAccountLinkComponent, ReplyDialogComponent],
  templateUrl: './support-reports.component.html',
  styleUrl: './support-reports.component.scss',
})
export class SupportReportsComponent implements OnInit {
  private _operatorIssueReportsService = inject(OperatorIssueReportsService);
  private _operatorAppRatingsService = inject(OperatorAppRatingsService);
  private _operatorSupportReplyService = inject(OperatorSupportReplyService);
  private _operatorSupportHandledService = inject(OperatorSupportHandledService);
  private _apiUrl = inject(API_URL);

  loading = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  issueReports: IOperatorIssueReportVm[] = [];
  appRatings: IOperatorAppRatingVm[] = [];

  /** By default only what still waits for an answer is listed; the operator can switch to everything. */
  showAll = false;
  readonly filterOptions = [
    { label: 'Bez odpowiedzi', value: false },
    { label: 'Wszystkie', value: true },
  ];
  visibleIssueReports: IOperatorIssueReportVm[] = [];
  visibleAppRatings: IOperatorAppRatingVm[] = [];

  /** Items with a "marked as answered" change in flight, keyed by kind and id. */
  handledPending = new Set<string>();
  /** The item just marked as answered without a message - the banner offers to take the mark back. */
  undoHandled: { item: SupportItem; kind: SupportKind } | null = null;

  replyVisible = false;
  replyHeader = '';
  replyTarget: IReplyTarget | null = null;
  replySubject = '';
  replyBody = '';
  replying = false;
  replyError: string | null = null;

  ngOnInit(): void {
    this.load();
  }

  refresh(): void {
    this.successMessage = null;
    this.undoHandled = null;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.errorMessage = null;
    forkJoin([this._operatorIssueReportsService.get(), this._operatorAppRatingsService.get()]).subscribe({
      next: ([issueReports, appRatings]) => {
        this.issueReports = issueReports;
        this.appRatings = appRatings;
        this.applyFilter();
        this.loading = false;
      },
      error: (e) => {
        this.errorMessage = e?.error?.message ?? 'Nie udało się pobrać zgłoszeń.';
        this.loading = false;
      },
    });
  }

  applyFilter(): void {
    this.visibleIssueReports = this.showAll ? this.issueReports : this.issueReports.filter((x) => this.awaitsReply(x));
    // A rating with stars only says nothing to answer to.
    this.visibleAppRatings = this.showAll
      ? this.appRatings
      : this.appRatings.filter((x) => !!x.feedback?.trim() && this.awaitsReply(x));
  }

  /**
   * Not answered yet - neither by e-mail nor marked as answered without a message - and answerable: something
   * sent without signing in has no address to reply to, so it would never leave the list; it is shown under
   * "Wszystkie" only.
   */
  awaitsReply(item: SupportItem): boolean {
    return !!item.createdByEmail && !item.replies?.length && !item.handledAt;
  }

  /** Marks the item as answered without sending a message (it leaves "Bez odpowiedzi"), or takes the mark back. */
  setHandled(item: SupportItem, kind: SupportKind, handled: boolean): void {
    const key = `${kind}-${item.id}`;
    if (this.handledPending.has(key)) return;

    this.handledPending.add(key);
    this.errorMessage = null;
    this._operatorSupportHandledService
      .put({
        issueReportId: kind === 'issue' ? item.id : undefined,
        appRatingId: kind === 'rating' ? item.id : undefined,
        handled,
      })
      .subscribe({
        next: (result) => {
          this.handledPending.delete(key);
          item.handledAt = result.handledAt;
          item.handledByEmail = result.handledByEmail;
          this.applyFilter();
          this.undoHandled = handled ? { item, kind } : null;
          this.successMessage = handled
            ? 'Oznaczono jako odpowiedziane, bez wysyłania wiadomości.'
            : 'Cofnięto oznaczenie, pozycja znowu czeka na odpowiedź.';
        },
        error: (e) => {
          this.handledPending.delete(key);
          this.errorMessage = e?.error?.message ?? 'Nie udało się zmienić oznaczenia.';
        },
      });
  }

  isHandledPending(item: SupportItem, kind: SupportKind): boolean {
    return this.handledPending.has(`${kind}-${item.id}`);
  }

  tabHeader(label: string, visible: unknown[], all: unknown[]): string {
    return this.showAll ? `${label} (${all.length})` : `${label} (${visible.length} z ${all.length})`;
  }

  openIssueReply(report: IOperatorIssueReportVm): void {
    if (!report.createdByEmail) return;

    this._openReply(
      { issueReportId: report.id, email: report.createdByEmail, userGuid: report.createdByGuid },
      'Odpowiedź na zgłoszenie błędu',
      'ZdamyPrawko – odpowiedź na zgłoszenie',
      buildReplyBody('dziękujemy za zgłoszenie.', {
        header: `Twoje zgłoszenie z ${this._formatDate(report.created)}:`,
        text: report.description,
      }),
    );
  }

  openRatingReply(rating: IOperatorAppRatingVm): void {
    if (!rating.createdByEmail) return;

    this._openReply(
      { appRatingId: rating.id, email: rating.createdByEmail, userGuid: rating.createdByGuid },
      'Odpowiedź na opinię',
      'ZdamyPrawko – odpowiedź na Twoją opinię',
      buildReplyBody('dziękujemy za opinię.', {
        header: `Twoja ocena z ${this._formatDate(rating.created)}: ${this.stars(rating.rating)}`,
        text: rating.feedback,
      }),
    );
  }

  sendReply(): void {
    const target = this.replyTarget;
    if (!target || this.replying) return;

    this.replying = true;
    this.replyError = null;
    this._operatorSupportReplyService
      .post({
        issueReportId: target.issueReportId,
        appRatingId: target.appRatingId,
        subject: this.replySubject.trim(),
        body: this.replyBody.trim(),
      })
      .subscribe({
        next: () => {
          this.replying = false;
          this.replyVisible = false;
          this.successMessage = `Wysłano odpowiedź do ${target.email}.`;
          this.undoHandled = null;
          // Both lists carry the replies, and the dates are shown the way the server stores them.
          this.load();
        },
        error: (e) => {
          this.replying = false;
          this.replyError = e?.error?.message ?? 'Nie udało się wysłać odpowiedzi.';
        },
      });
  }

  lastReply(item: SupportItem): ISupportReplyVm | undefined {
    return item.replies?.at(-1);
  }

  stars(rating: number): string {
    return '★'.repeat(rating) + '☆'.repeat(Math.max(0, 5 - rating));
  }

  /** Attachments are stored either as absolute URLs or as paths on the API. */
  resolveUrl(url?: string): string | undefined {
    if (!url) {
      return undefined;
    }
    return url.startsWith('http') ? url : `${this._apiUrl}${url}`;
  }

  private _openReply(target: Omit<IReplyTarget, 'thread'>, header: string, subject: string, body: string): void {
    this.replyTarget = { ...target, thread: this._buildThread(target) };
    this.replyHeader = header;
    this.replySubject = subject;
    this.replyBody = body;
    this.replyError = null;
    this.successMessage = null;
    this.undoHandled = null;
    this.replyVisible = true;
  }

  /** Everything this user sent us from the app, with our answers, oldest first. */
  private _buildThread(target: Omit<IReplyTarget, 'thread'>): IReplyThreadItem[] {
    const isAuthor = (x: IOperatorIssueReportVm | IOperatorAppRatingVm) =>
      target.userGuid ? x.createdByGuid === target.userGuid : x.createdByEmail === target.email;
    const answers = (replies?: ISupportReplyVm[]) =>
      (replies ?? []).map((x) => ({
        at: x.sentAt,
        outgoing: true,
        label: x.sentByEmail ? `odpowiedź · ${x.sentByEmail}` : 'odpowiedź',
        subject: x.subject,
        text: x.body,
      }));

    return [
      ...this.issueReports.filter(isAuthor).flatMap((x) => [
        {
          at: x.created,
          outgoing: false,
          label: 'zgłoszenie błędu',
          text: x.description,
          current: x.id === target.issueReportId,
        },
        ...answers(x.replies),
      ]),
      ...this.appRatings.filter(isAuthor).flatMap((x) => [
        {
          at: x.created,
          outgoing: false,
          label: `ocena ${this.stars(x.rating)}`,
          text: x.feedback || '(bez opinii)',
          current: x.id === target.appRatingId,
        },
        ...answers(x.replies),
      ]),
    ].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  }

  private _formatDate(value: string): string {
    return formatDate(value, 'yyyy-MM-dd HH:mm', 'en-US');
  }
}
